import axios from "axios";
import { DEMO } from "@/demo/demo";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const TOKEN_KEY = "traceqc_token";
export const USER_KEY = "traceqc_user";

const authClient = axios.create({ baseURL: `${BASE}/api/auth` });
const signupClient = axios.create({ baseURL: `${BASE}/api/signup` });

// The refresh token is held in memory only (a module-level variable), never
// written to sessionStorage — sessionStorage is trivially readable by any
// injected script with a one-line `sessionStorage.getItem(...)`, and it
// persists for as long as the tab is open, giving a delayed-read XSS payload
// a long window to exfiltrate it (12h TTL vs the 1h access token). A
// module-scoped variable isn't perfectly XSS-proof either (arbitrary script
// execution in the page can still reach it), but it closes off the trivial
// exfiltration path and vanishes on reload/tab close instead of persisting.
// Trade-off: a page reload after the 1h access token expires forces a fresh
// login instead of a silent refresh (previously the session survived reloads
// for up to REFRESH_EXPIRE_HOURS via sessionStorage).
let _refreshToken = null;

// One-time cleanup: a tab that had the previous version of this file loaded
// wrote a real, still-backend-valid refresh token to sessionStorage under
// this key. Remove it on module load so it doesn't linger, XSS-readable,
// for the rest of that tab's life just because no code reads it anymore.
sessionStorage.removeItem("traceqc_refresh");

const storeSession = (data) => {
  sessionStorage.setItem(TOKEN_KEY, data.token);
  sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
  if (data.refresh_token) _refreshToken = data.refresh_token;
};

export const login = async (username, password) => {
  const r = await authClient.post("/login", { username, password });
  if (r.data.require_totp) {
    // TOTP second factor required — do NOT store token yet
    return r.data;
  }
  storeSession(r.data);
  return r.data;
};

export const signup = async ({ username, email, password, name, company_name }) => {
  const r = await signupClient.post("", { username, email, password, name, company_name });
  return r.data;
};

export const verifyEmail = async (token) => {
  const r = await signupClient.get("/verify", { params: { token } });
  return r.data;
};

export const resendVerification = async (email) => {
  const r = await signupClient.post("/resend-verification", null, { params: { email } });
  return r.data;
};

export const totpLogin = async (totp_session, code) => {
  const r = await authClient.post("/totp/login", { totp_session, code });
  storeSession(r.data);
  return r.data;
};

export const getRefreshToken = () => _refreshToken;

// Exchange the stored refresh token for a fresh access token (rotates the
// refresh token too). Returns the new access token, or null if refresh failed.
// A bare axios call is used so it never recurses through the API client's
// 401 interceptor.
export const refreshSession = async () => {
  const refresh_token = getRefreshToken();
  if (!refresh_token) return null;
  try {
    const r = await authClient.post("/refresh", { refresh_token });
    sessionStorage.setItem(TOKEN_KEY, r.data.token);
    if (r.data.refresh_token) _refreshToken = r.data.refresh_token;
    // The new token's claims come from a fresh DB read (see auth.py's
    // /refresh handler) — re-sync the cached user + notify AuthContext now,
    // not just the token, so a permission change picked up by this refresh
    // doesn't sit invisible until the next full page reload. Fire-and-forget:
    // never block the caller's actual retried request on this.
    syncUser();
    return r.data.token;
  } catch (_) {
    return null;
  }
};

export const totpSetup = async () => {
  const token = getToken();
  const r = await authClient.post("/totp/setup", null, { headers: { Authorization: `Bearer ${token}` } });
  return r.data;
};

export const totpConfirm = async (code, currentPassword) => {
  const token = getToken();
  const r = await authClient.post(
    "/totp/confirm",
    { code, current_password: currentPassword },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return r.data;
};

export const totpDisable = async (code) => {
  const token = getToken();
  const r = await authClient.delete("/totp", { data: { code }, headers: { Authorization: `Bearer ${token}` } });
  return r.data;
};

export const logout = async () => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  // Clear local state first so the app treats the user as logged out immediately,
  // even if the server revocation call takes time or fails.
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  _refreshToken = null;
  if (token) {
    // Tell the server to revoke this token (best-effort — don't block on failure).
    try {
      await authClient.post("/logout", null, { headers: { Authorization: `Bearer ${token}` } });
    } catch (_) { /* ignore network errors on logout */ }
  }
};

export const getToken = () => sessionStorage.getItem(TOKEN_KEY);

// Re-sync the cached user object from the server's own view of the session
// (GET /me — the decoded, currently-valid token claims). The cached copy is
// otherwise written ONLY at login and never refreshed, so a session that
// predates a permission field being added (e.g. is_admin) never sees it until
// the user logs out and back in — a real incident (see ADMIN-DELETE-OVERRIDE-2:
// the admin-delete prompt silently never rendered for a pre-existing session).
// Best-effort: on any failure (expired token, network), leave the cached user
// untouched — the shared API client's own 401 interceptor already handles an
// actually-invalid token on the next real request.
//
// Live-verified 2-Sep: GET /me only decodes the CURRENT access token's own
// claims (backend does no DB lookup — see auth.py::get_current_user), and a
// DB-side permission change (role/is_admin edited while the user's token is
// still valid, the common case within its 1h TTL) does NOT appear here until
// that token itself is reissued. `/refresh` DOES re-fetch the user from DB
// and mint fresh claims — so `refreshSession()` below fires this same sync
// once it has a new token, closing the gap for the case that actually
// happens most often (silent 401-triggered refresh mid-session).
export const syncUser = async () => {
  if (DEMO) return getStoredUser();
  const token = getToken();
  if (!token) return null;
  try {
    const r = await authClient.get("/me", { headers: { Authorization: `Bearer ${token}` } });
    const claims = r.data || {};
    const merged = {
      ...getStoredUser(),
      username: claims.sub,
      role: claims.role,
      name: claims.name,
      is_admin: claims.is_admin,
      company_id: claims.company_id,
      companies: claims.companies,
      all_companies: claims.all_companies,
    };
    sessionStorage.setItem(USER_KEY, JSON.stringify(merged));
    // AuthContext's `user` state is otherwise only ever set at login/initial
    // sync — nothing else observes sessionStorage changing, so a refresh
    // that happens deep inside an unrelated API call (the 401 interceptor)
    // would silently update the token but leave every already-rendered
    // permission-gated element (e.g. the admin-delete prompt) stale until
    // the next full reload. A plain DOM event needs no new dependency and
    // reaches AuthContext whether or not it happens to be the caller here.
    window.dispatchEvent(new CustomEvent("traceqc:user-synced", { detail: merged }));
    return merged;
  } catch (_) {
    return null;
  }
};

export const getStoredUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
};
